import { apiClientSilent } from "api/trpc/trpc-client";
import { ProposalDto } from "../../app-services/proposal/models/ProposalDto";
import { IDBEntityCacheStore } from "./IDBEntityCacheStore";
import { Timeouts } from "@gap/utils/date-utils";
import { GapError } from "@trpc-endpoints/trpc/error-handler/GapError";
import { UserOrgWithOrgDto } from "@gap/backend/app-services/user-org/models/UserOrgWithOrgDto";

export class IDBProposalEntityCacheStore {
   /**
    * Get proposal by id. If the proposal is not accepted, it will fetch the latest proposal from the server.
    * If the proposal is accepted, it will return the cached proposal.
    */
   static getById = async (proposalId: string, orgInfo: UserOrgWithOrgDto): Promise<ProposalDto | undefined> => {
      const proposalCache = new IDBEntityCacheStore<ProposalDto>({
         entityKey: `proposal-${proposalId}`,
         fetcher: () => apiClientSilent.proposals.getProposalById.query({ proposalId }),
         cacheOptions: {
            ttlMs: Timeouts.day * 30
         }
      })

      let proposal: ProposalDto | undefined = undefined

      const proposalExistInCache = !!(await proposalCache.isSet)
      proposal = await proposalCache.fetch() || undefined
      if (proposal && !proposal.acceptedAt && proposalExistInCache) {
         proposal = await proposalCache.fetch(true) || undefined
      }

      // For client teammembers / guest we need to check permission should the permission have been revoked
      // Don't check or invalidate if we are a service user
      if (orgInfo.orgType !== 'advisor' && orgInfo.roleName !== 'Admin' && !proposalCache.isServiceUser) {
         if (!orgInfo.permissions?.includes(`proposal-${proposalId}`)) {
            await proposalCache.invalidate()
            throw new GapError.forbidden(GapError.messages.forbidden.noDocumentAccessUser)
         }
      }

      // Remove email from step 1 for contacts of the proposal
      if (proposal && !!proposal.acceptedAt && !proposalCache.isServiceUser) {
         proposal.step1?.contacts?.forEach(contact => {
            contact.email = ''
         })
         await proposalCache.update(proposal)
      }

      // Don't cache unaceppted proposals
      if (proposal && !proposal.acceptedAt) {
         await proposalCache.invalidate()
      }

      return proposal
   }
}
